import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FiTv } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import type HlsClass from 'hls.js';
import xtreamAPI, { XtreamContent } from '../../api/xtreamAPI';
import { useLiveHistory } from '../../contexts/LiveHistoryContext';
import {
  PlaybackOverlayStatus,
  PlaybackStatusOverlay,
} from '../PlaybackStatusOverlay/PlaybackStatusOverlay';
import { togglePictureInPicture } from '../../utils/pictureInPicture';
import { selectHlsTrack } from '../../utils/hlsTrackSelection';
import { getLiveBufferMinutes } from '../../utils/liveBuffer';
import './MiniPlayer.css';

// HLS.js loaded via CDN in public/index.html for Chrome 47 compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Hls = (window as any).Hls as typeof HlsClass;

export interface AudioTrackInfo {
  id: number;
  name: string;
  lang?: string;
}

export interface SubtitleTrackInfo {
  id: number;
  name: string;
  lang?: string;
  forced?: boolean;
}

export interface LiveTimeshiftInfo {
  windowStart: number;
  liveEdge: number;
  currentTime: number;
}

export type PlaybackEngine = 'ts' | 'hls' | 'native-hls' | 'avplay';

export interface MiniPlayerHandle {
  selectAudioTrack(trackId: number): Promise<void>;
  selectSubtitleTrack(trackId: number): Promise<void>;
  togglePictureInPicture(): Promise<boolean>;
  play(): void;
  pause(): void;
  togglePlayPause(): void;
  stop(): void;
  seekLiveBy(seconds: number): boolean;
}

interface MiniPlayerProps {
  channel: XtreamContent | null;
  /** Registra o canal no histórico somente quando a reprodução ocorre dentro da Live TV. */
  recordLiveHistory?: boolean;
  /** Chamado quando o usuário clica na área do player */
  onOpenLiveTV?: () => void;
  /** Throughput atual aproximado do stream em Mbps */
  onThroughputMbps?: (mbps: number | null) => void;
  /** Estilos inline aplicados ao <video> (ex: objectFit, aspectRatio, width, margin) */
  videoStyle?: React.CSSProperties;
  /** Chamado quando a resolução do vídeo é conhecida */
  onResolutionChange?: (width: number, height: number) => void;
  /** Janela real disponível para voltar/avançar no canal ao vivo. */
  onTimeshiftChange?: (info: LiveTimeshiftInfo | null) => void;
  /** Chamado quando as faixas de áudio disponíveis mudam */
  onAudioTracksChange?: (tracks: AudioTrackInfo[], activeId: number) => void;
  /** Chamado quando as faixas de legenda disponíveis mudam */
  onSubtitleTracksChange?: (tracks: SubtitleTrackInfo[], activeId: number) => void;
  /** Chamado quando o engine de playback muda: 'ts' = MPEG-TS nativo, 'hls' = HLS.js, 'native-hls' = HLS nativo do browser */
  onEngineChange?: (engine: PlaybackEngine | null) => void;
}

const START_TIMEOUT_MS = 18000;
const STALL_TIMEOUT_MS = 10000;
const MAX_RECONNECT_DELAY_MS = 10000;
const DEBUG_MINI_PLAYER = false;

const getLiveTimeshiftInfo = (video: HTMLVideoElement, maxBufferSeconds: number): LiveTimeshiftInfo | null => {
  const ranges = video.buffered;
  for (let i = ranges.length - 1; i >= 0; i -= 1) {
    const start = ranges.start(i);
    const liveEdge = ranges.end(i);
    if (!Number.isFinite(start) || !Number.isFinite(liveEdge) || liveEdge <= start) continue;
    if (video.currentTime < start - 0.1 || video.currentTime > liveEdge + 0.1) continue;
    const windowStart = Math.max(start, liveEdge - maxBufferSeconds);
    return {
      windowStart,
      liveEdge,
      currentTime: Math.max(windowStart, Math.min(liveEdge, video.currentTime)),
    };
  }
  return null;
};

export const MiniPlayer = React.forwardRef<MiniPlayerHandle, MiniPlayerProps>(function MiniPlayer(
  { channel, recordLiveHistory = false, onOpenLiveTV, onThroughputMbps, videoStyle, onResolutionChange, onTimeshiftChange, onAudioTracksChange, onSubtitleTracksChange, onEngineChange },
  ref
) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const onResolutionRef = useRef(onResolutionChange);
  const onEngineChangeRef = useRef(onEngineChange);
  useEffect(() => { onEngineChangeRef.current = onEngineChange; }, [onEngineChange]);
  useEffect(() => { onResolutionRef.current = onResolutionChange; }, [onResolutionChange]);
  const hlsRef = useRef<HlsClass | null>(null);
  const maxBufferSecondsRef = useRef(getLiveBufferMinutes() * 60);
  const activeEngineRef = useRef<PlaybackEngine | null>(null);
  const onThroughputRef = useRef<MiniPlayerProps['onThroughputMbps']>(onThroughputMbps);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const videoListenersCleanupRef = useRef<(() => void) | null>(null);
  const hasMarkedWatchedRef = useRef<string | null>(null);
  const recordLiveHistoryRef = useRef(recordLiveHistory);
  const isActuallyPlayingRef = useRef(false);
  const startTimeoutRef = useRef<number | null>(null);
  const audioTracksRef = useRef<AudioTrackInfo[]>([]);
  const activeAudioIdRef = useRef<number>(0);
  const subtitleTracksRef = useRef<SubtitleTrackInfo[]>([]);
  const activeSubtitleIdRef = useRef<number>(-1);
  const forcedSubtitleAppliedRef = useRef(false);
  const onAudioTracksRef = useRef(onAudioTracksChange);
  const onSubtitleTracksRef = useRef(onSubtitleTracksChange);
  const onTimeshiftChangeRef = useRef(onTimeshiftChange);
  const lastTimeshiftRef = useRef('');

  const { markWatched } = useLiveHistory();
  const [status, setStatus] = useState<PlaybackOverlayStatus | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const activeVariantIndexRef = useRef(0);

  const streamId = useMemo(() => String(channel?.stream_id || ''), [channel]);
  const streamVariants = useMemo(() => {
    // Demo channels provide a direct URL — skip Xtream URL construction entirely.
    if (channel?.direct_source) return [xtreamAPI.preparePlaybackUrl(channel.direct_source)];
    if (!streamId) return [];
    return xtreamAPI.getLiveStreamURLVariants(streamId, ['m3u8', 'ts']);
  }, [channel, streamId]);

  useEffect(() => {
    onThroughputRef.current = onThroughputMbps;
  }, [onThroughputMbps]);

  useEffect(() => { onAudioTracksRef.current = onAudioTracksChange; }, [onAudioTracksChange]);
  useEffect(() => { onSubtitleTracksRef.current = onSubtitleTracksChange; }, [onSubtitleTracksChange]);
  useEffect(() => { onTimeshiftChangeRef.current = onTimeshiftChange; }, [onTimeshiftChange]);
  useEffect(() => { recordLiveHistoryRef.current = recordLiveHistory; }, [recordLiveHistory]);

  const publishTimeshiftInfo = useCallback((info: LiveTimeshiftInfo | null) => {
    const key = info
      ? `${Math.round(info.windowStart)}:${Math.round(info.liveEdge)}:${Math.round(info.currentTime * 2)}`
      : '';
    if (key === lastTimeshiftRef.current) return;
    lastTimeshiftRef.current = key;
    onTimeshiftChangeRef.current?.(info);
  }, []);

  const publishTimeshift = useCallback((video: HTMLVideoElement) => {
    publishTimeshiftInfo(getLiveTimeshiftInfo(video, maxBufferSecondsRef.current));
  }, [publishTimeshiftInfo]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearStartTimeout = useCallback(() => {
    if (startTimeoutRef.current !== null) {
      window.clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
  }, []);

  const destroyPlayback = useCallback(() => {
    clearReconnectTimer();
    clearStartTimeout();
    const avplay = (window as any).webapis?.avplay;
    if (activeEngineRef.current === 'avplay' && avplay) {
      try { avplay.stop(); } catch { /* already stopped */ }
      try { avplay.close(); } catch { /* already closed */ }
    }
    activeEngineRef.current = null;
    onEngineChangeRef.current?.(null);
    onThroughputRef.current?.(null);
    audioTracksRef.current = [];
    activeAudioIdRef.current = 0;
    subtitleTracksRef.current = [];
    activeSubtitleIdRef.current = -1;
    forcedSubtitleAppliedRef.current = false;
    onAudioTracksRef.current?.([], 0);
    onSubtitleTracksRef.current?.([], -1);
    lastTimeshiftRef.current = '';
    onTimeshiftChangeRef.current?.(null);
    isActuallyPlayingRef.current = false;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const video = videoRef.current;
    if (video) {
      videoListenersCleanupRef.current?.();
      videoListenersCleanupRef.current = null;
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
  }, [clearReconnectTimer, clearStartTimeout]);

  const showStatus = useCallback((nextStatus: PlaybackOverlayStatus, message?: string) => {
    setStatus(nextStatus);
    setStatusMessage(message || '');
  }, []);

  const pushDebug = useCallback((message: string) => {
    if (!DEBUG_MINI_PLAYER) return;
    setDebugInfo((prev) => [`${new Date().toLocaleTimeString()} ${message}`, ...prev].slice(0, 8));
  }, []);

  const hideStatus = useCallback(() => {
    setStatus(null);
    setStatusMessage('');
  }, []);

  const markCurrentChannelWatched = useCallback(() => {
    if (!recordLiveHistoryRef.current || !channel || !streamId || hasMarkedWatchedRef.current === streamId) return;
    markWatched({
      streamId,
      channelName: channel.name,
      icon: channel.icon || channel.stream_icon,
      categoryId: channel.category_id,
      containerExtension: channel.container_extension,
    });
    hasMarkedWatchedRef.current = streamId;
  }, [channel, markWatched, streamId]);

  // A transmissão pode ter começado na Home. Ao entrar na Live TV, registra o
  // canal sem reiniciar o vídeo, desde que ele já esteja efetivamente tocando.
  useEffect(() => {
    if (recordLiveHistory && isActuallyPlayingRef.current) {
      markCurrentChannelWatched();
    }
  }, [markCurrentChannelWatched, recordLiveHistory]);

  const safePlay = useCallback((video: HTMLVideoElement) => {
    try {
      const maybePromise = video.play() as Promise<void> | void;
      if (maybePromise && typeof (maybePromise as Promise<void>).catch === 'function') {
        (maybePromise as Promise<void>).catch((err: any) => {
          const mayBeNotAllowed = /not allowed|NotAllowedError|Autoplay|DOMException/i.test(
            err?.message || err?.name || ''
          );

          if (mayBeNotAllowed) {
            try {
              const prevMuted = video.muted;
              video.muted = true;
              const retryPromise = video.play() as Promise<void> | void;
              if (retryPromise && typeof (retryPromise as Promise<void>).catch === 'function') {
                (retryPromise as Promise<void>).catch(() => {
                  video.muted = prevMuted;
                });
              }
            } catch {
              // noop
            }
          }
        });
        return;
      }

      if (maybePromise === undefined) {
        try {
          video.play();
        } catch {
          // noop
        }
      }
    } catch {
      // noop
    }
  }, []);

  const startPlayback = useCallback((isReconnect = false) => {
    const video = videoRef.current;
    if (!video || !channel || !streamId || !streamVariants.length) return;

    destroyPlayback();

    showStatus(isReconnect ? 'reconnecting' : 'connecting');

    const storedEngine = localStorage.getItem('ott_live_engine');
    const enginePref = storedEngine === 'ts' || storedEngine === 'hls' || storedEngine === 'native-hls'
      ? storedEngine
      : 'auto';
    const decodedVariants = streamVariants.map((variant) => xtreamAPI.decodeStreamUrl(variant));
    const hlsUrls = decodedVariants.filter((variant) => variant.includes('.m3u8'));
    const tsUrls = decodedVariants.filter((variant) => variant.includes('.ts'));
    const avplay = (window as any).webapis?.avplay;
    const maxBufferSeconds = getLiveBufferMinutes() * 60;
    maxBufferSecondsRef.current = maxBufferSeconds;
    const isSmartTv = /Tizen|SMART-TV|Web0S|WebOS|NetCast/i.test(navigator.userAgent) || Boolean((window as any).webOS);
    const nativeHlsSupported = Boolean(
      video.canPlayType('application/vnd.apple.mpegurl') ||
      video.canPlayType('application/x-mpegURL') ||
      isSmartTv
    );
    const candidates: Array<{ engine: PlaybackEngine; url: string }> = [];
    const addCandidates = (engine: PlaybackEngine, urls: string[]) => {
      urls.forEach((url) => candidates.push({ engine, url }));
    };

    if (enginePref === 'auto') {
      if (isSmartTv) {
        if (nativeHlsSupported) addCandidates('native-hls', hlsUrls);
        addCandidates('ts', tsUrls);
      } else {
        if (Hls.isSupported()) addCandidates('hls', hlsUrls);
        if (nativeHlsSupported) addCandidates('native-hls', hlsUrls);
        if (video.canPlayType('video/mp2t')) addCandidates('ts', tsUrls);
      }
    } else if (enginePref === 'hls' && Hls.isSupported()) addCandidates('hls', hlsUrls);
    else if (enginePref === 'native-hls') addCandidates('native-hls', hlsUrls);
    else if (enginePref === 'ts') addCandidates('ts', tsUrls);

    const candidate = candidates[activeVariantIndexRef.current % candidates.length];
    if (!candidate) {
      showStatus('error');
      return;
    }
    const { engine, url: sourceUrl } = candidate;
    pushDebug(`start ${isReconnect ? 'reconnect' : 'connect'} variant=${activeVariantIndexRef.current + 1}/${streamVariants.length}`);
    pushDebug(`engine ${enginePref} -> ${engine}`);
    pushDebug(`url ${sourceUrl}`);

    const handleFatalError = () => {
      if (reconnectTimerRef.current !== null) return;
      clearStartTimeout();
      reconnectAttemptsRef.current += 1;
      activeVariantIndexRef.current = (activeVariantIndexRef.current + 1) % candidates.length;
      const reconnectDelay = Math.min(1500 * reconnectAttemptsRef.current, MAX_RECONNECT_DELAY_MS);
      pushDebug(`fatal -> attempt=${reconnectAttemptsRef.current}`);
      showStatus('reconnecting');
      clearReconnectTimer();
      reconnectTimerRef.current = window.setTimeout(() => {
        startPlayback(true);
      }, reconnectDelay);
    };

    const armStallReconnect = () => {
      if (startTimeoutRef.current !== null) return;
      startTimeoutRef.current = window.setTimeout(() => {
        pushDebug('stall timeout');
        handleFatalError();
      }, STALL_TIMEOUT_MS);
    };

    const onPlaying = () => {
      clearStartTimeout();
      reconnectAttemptsRef.current = 0;
      isActuallyPlayingRef.current = true;
      if (enginePref === 'auto') localStorage.setItem('ott_live_engine', engine);
      pushDebug('event playing');
      markCurrentChannelWatched();
      hideStatus();
    };

    const onCanPlay = () => {
      pushDebug('event canplay');
    };

    const onWaiting = () => {
      pushDebug('event waiting');
      showStatus('buffering');
      armStallReconnect();
    };
    const onStalled = () => {
      pushDebug('event stalled');
      showStatus('buffering');
      armStallReconnect();
    };
    const onError = () => {
      const mediaError = video.error;
      pushDebug(`video error code=${mediaError?.code || 'unknown'}`);
      handleFatalError();
    };

    video.addEventListener('playing', onPlaying);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onStalled);
    video.addEventListener('error', onError);
    videoListenersCleanupRef.current = () => {
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onStalled);
      video.removeEventListener('error', onError);
    };

    clearStartTimeout();
    startTimeoutRef.current = window.setTimeout(() => {
      pushDebug('start timeout');
      handleFatalError();
    }, START_TIMEOUT_MS);

    if (engine === 'avplay') {
      activeEngineRef.current = 'avplay';
      onEngineChangeRef.current?.('avplay');
      try {
        avplay.open(sourceUrl);
        avplay.setListener({
          onbufferingstart: () => {
            showStatus('buffering');
            armStallReconnect();
          },
          onbufferingcomplete: () => {},
          oncurrentplaytime: () => {
            if (!isActuallyPlayingRef.current) onPlaying();
            else {
              clearStartTimeout();
              hideStatus();
            }
            try {
              const range = String(avplay.getStreamingProperty('GET_LIVE_DURATION')).split('|').map(Number);
              const currentTime = Number(avplay.getCurrentTime());
              if (range.length >= 2 && range.every(Number.isFinite) && Number.isFinite(currentTime)) {
                publishTimeshiftInfo({
                  windowStart: Math.max(range[0], range[1] - maxBufferSeconds * 1000) / 1000,
                  liveEdge: range[1] / 1000,
                  currentTime: currentTime / 1000,
                });
              }
            } catch { /* live DVR range unavailable */ }
          },
          onstreamcompleted: handleFatalError,
          onerror: handleFatalError,
        });
        avplay.setDisplayRect(0, 0, 1920, 1080);
        avplay.setDisplayMethod('PLAYER_DISPLAY_MODE_LETTER_BOX');
        avplay.setBufferingParam('PLAYER_BUFFER_FOR_PLAY', 'PLAYER_BUFFER_SIZE_IN_SECOND', 4);
        avplay.setBufferingParam('PLAYER_BUFFER_FOR_RESUME', 'PLAYER_BUFFER_SIZE_IN_SECOND', 10);
        avplay.prepareAsync(() => {
          try { avplay.play(); } catch { handleFatalError(); }
        }, handleFatalError);
      } catch {
        handleFatalError();
      }
      return;
    }

    if (engine === 'ts') {
      pushDebug('native mpegts path (forced)');
      activeEngineRef.current = 'ts';
      onEngineChangeRef.current?.('ts');
      video.src = sourceUrl;
      video.preload = 'auto';
      safePlay(video);
      return;
    }

    if (engine === 'native-hls') {
      pushDebug('native hls path (forced)');
      activeEngineRef.current = 'native-hls';
      onEngineChangeRef.current?.('native-hls');
      video.src = sourceUrl;
      video.preload = 'auto';
      safePlay(video);
      return;
    }

    const hls = new Hls({
      enableWorker: typeof Worker !== 'undefined',
      lowLatencyMode: false,
      backBufferLength: maxBufferSeconds,
      maxBufferLength: maxBufferSeconds,
      maxMaxBufferLength: maxBufferSeconds,
      liveSyncDurationCount: isSmartTv ? 2 : 3,
      maxBufferHole: 0.5,
      manifestLoadingMaxRetry: 3,
      manifestLoadingRetryDelay: 500,
    });
    hlsRef.current = hls;
    activeEngineRef.current = 'hls';
    onEngineChangeRef.current?.('hls');
    pushDebug('hls created');

    try {
      hls.loadSource(sourceUrl);
      hls.attachMedia(video);
      pushDebug('hls loadSource + attachMedia');
    } catch {
      pushDebug('hls attach failed');
      handleFatalError();
      return;
    }

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      pushDebug('hls manifest parsed');
      safePlay(video);
    });

    hls.on(Hls.Events.FRAG_LOADED, (_event, data: any) => {
      pushDebug('hls fragment loaded');

      const estimateBps = Number((hls as any).bandwidthEstimate || 0);
      if (Number.isFinite(estimateBps) && estimateBps > 0) {
        onThroughputRef.current?.(estimateBps / 1000000);
        return;
      }

      const loadedBytes = Number(data?.stats?.total || data?.stats?.loaded || 0);
      const start = Number(
        data?.stats?.loading?.start ||
          data?.stats?.parsing?.start ||
          data?.stats?.trequest ||
          0
      );
      const end = Number(
        data?.stats?.loading?.end ||
          data?.stats?.parsing?.end ||
          data?.stats?.tbuffered ||
          data?.stats?.tload ||
          0
      );
      const elapsedMs = Math.max(1, end - start);

      if (loadedBytes > 0 && elapsedMs > 0) {
        const mbps = (loadedBytes * 8) / (elapsedMs / 1000) / 1000000;
        if (Number.isFinite(mbps) && mbps > 0) {
          onThroughputRef.current?.(mbps);
        }
      }
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      pushDebug(`hls error fatal=${data?.fatal ? 'yes' : 'no'} details=${String(data?.details || 'unknown')}`);
      if (data?.fatal) {
        handleFatalError();
        return;
      }

      if (data?.details && /buffer/i.test(String(data.details))) {
        showStatus('buffering');
        armStallReconnect();
      }
    });

    hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, (_evt: any, data: any) => {
      const tracks: AudioTrackInfo[] = (data?.audioTracks || []).map((track: any) => ({
        id: track.id,
        name: track.name || track.lang || t('playback.audioTrack', { num: track.id + 1 }),
        lang: track.lang,
      }));
      audioTracksRef.current = tracks;
      activeAudioIdRef.current = hls.audioTrack >= 0 ? hls.audioTrack : 0;
      onAudioTracksRef.current?.(tracks, activeAudioIdRef.current);
    });

    hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, (_evt: any, data: any) => {
      const newId: number = typeof data?.id === 'number' ? data.id : hls.audioTrack;
      activeAudioIdRef.current = newId;
      onAudioTracksRef.current?.(audioTracksRef.current, newId);
    });

    hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, (_evt: any, data: any) => {
      const tracks: SubtitleTrackInfo[] = (data?.subtitleTracks || []).map((track: any) => ({
        id: track.id,
        name: track.name || track.lang || t('playback.subtitleTrack', { num: track.id + 1 }),
        lang: track.lang,
        forced: !!track.forced,
      }));
      subtitleTracksRef.current = tracks;
      const forced = forcedSubtitleAppliedRef.current ? undefined : tracks.find(track => track.forced);
      if (forced) forcedSubtitleAppliedRef.current = true;
      if (forced) hls.subtitleTrack = forced.id;
      activeSubtitleIdRef.current = forced?.id ?? hls.subtitleTrack;
      onSubtitleTracksRef.current?.(tracks, activeSubtitleIdRef.current);
    });

    hls.on(Hls.Events.SUBTITLE_TRACK_SWITCH, (_evt: any, data: any) => {
      const newId: number = typeof data?.id === 'number' ? data.id : hls.subtitleTrack;
      activeSubtitleIdRef.current = newId;
      onSubtitleTracksRef.current?.(subtitleTracksRef.current, newId);
    });

    // Update resolution badge whenever ABR switches to a different quality level.
    hls.on(Hls.Events.LEVEL_SWITCHED, (_evt: any, data: any) => {
      const level = hls.levels?.[data?.level];
      if (level?.width && level?.height) {
        onResolutionRef.current?.(level.width, level.height);
      } else {
        // Fallback: read from the video element directly
        const v = videoRef.current;
        if (v && v.videoWidth && v.videoHeight) {
          onResolutionRef.current?.(v.videoWidth, v.videoHeight);
        }
      }
    });
  }, [channel, clearReconnectTimer, clearStartTimeout, destroyPlayback, hideStatus, markCurrentChannelWatched, publishTimeshiftInfo, pushDebug, safePlay, showStatus, streamId, streamVariants, t]);

  useImperativeHandle(ref, () => ({
    selectAudioTrack(trackId: number) {
      return selectHlsTrack(hlsRef.current, 'audio', trackId);
    },
    selectSubtitleTrack(trackId: number) {
      return selectHlsTrack(hlsRef.current, 'subtitle', trackId);
    },
    togglePictureInPicture() {
      return togglePictureInPicture(videoRef.current);
    },
    play() {
      if (!activeEngineRef.current) {
        startPlayback(false);
        return;
      }
      if (activeEngineRef.current === 'avplay') {
        try { (window as any).webapis?.avplay?.play(); } catch { /* unavailable */ }
        return;
      }
      if (videoRef.current) safePlay(videoRef.current);
    },
    pause() {
      if (activeEngineRef.current === 'avplay') {
        try { (window as any).webapis?.avplay?.pause(); } catch { /* unavailable */ }
        return;
      }
      videoRef.current?.pause();
    },
    togglePlayPause() {
      if (activeEngineRef.current === 'avplay') {
        const avplay = (window as any).webapis?.avplay;
        try {
          if (avplay?.getState?.() === 'PAUSED') avplay.play();
          else avplay?.pause();
        } catch { /* unavailable */ }
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) safePlay(video);
      else video.pause();
    },
    stop() {
      destroyPlayback();
      hideStatus();
    },
    seekLiveBy(seconds: number) {
      if (activeEngineRef.current !== 'hls') return false;
      const video = videoRef.current;
      if (!video) return false;
      const info = getLiveTimeshiftInfo(video, maxBufferSecondsRef.current);
      if (!info) return false;
      try {
        const target = Math.max(info.windowStart, Math.min(info.liveEdge - 0.1, info.currentTime + seconds));
        if (Math.abs(target - info.currentTime) < 0.5) return false;
        video.currentTime = target;
        safePlay(video);
        publishTimeshift(video);
        return true;
      } catch {
        return false;
      }
    },
  }), [destroyPlayback, hideStatus, publishTimeshift, safePlay, startPlayback]);

  useEffect(() => {
    return () => {
      destroyPlayback();
    };
  }, [destroyPlayback]);

  useEffect(() => {
    hasMarkedWatchedRef.current = null;
    activeVariantIndexRef.current = 0;
    if (!streamId) {
      destroyPlayback();
      hideStatus();
      onThroughputRef.current?.(null);
      return;
    }

    reconnectAttemptsRef.current = 0;
    startPlayback(false);
  }, [destroyPlayback, hideStatus, startPlayback, streamId]);

  const handlePlayerClick = useCallback(() => {
    onOpenLiveTV?.();
  }, [onOpenLiveTV]);

  return (
    <div
      className="miniplayer"
      onClick={handlePlayerClick}
      role="button"
      tabIndex={0}
      aria-label={t('liveTV.openLiveTV')}
    >
      <object
        type="application/avplayer"
        className={`miniplayer__avplay${activeEngineRef.current === 'avplay' ? ' is-active' : ''}`}
        aria-hidden="true"
      >Samsung AVPlay</object>
      <video
        ref={videoRef}
        className={`miniplayer__video${activeEngineRef.current === 'avplay' ? ' is-hidden' : ''}`}
        playsInline
        controls={false}
        style={videoStyle}
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          if (v.videoWidth && v.videoHeight) {
            onResolutionRef.current?.(v.videoWidth, v.videoHeight);
          }
        }}
        onResize={(e) => {
          const v = e.currentTarget;
          if (v.videoWidth && v.videoHeight) {
            onResolutionRef.current?.(v.videoWidth, v.videoHeight);
          }
        }}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          if (!v.paused) {
            clearStartTimeout();
            hideStatus();
          }
          publishTimeshift(v);
        }}
        onProgress={(e) => publishTimeshift(e.currentTarget)}
        onSeeked={(e) => publishTimeshift(e.currentTarget)}
      />

      {!channel && (
        <div className="miniplayer__placeholder">
          <FiTv className="miniplayer__icon" />
          <span className="miniplayer__hint">{t('liveTV.loadingChannel')}</span>
        </div>
      )}

      {status && <PlaybackStatusOverlay status={status} message={statusMessage || undefined} />}

      {DEBUG_MINI_PLAYER && (
        <div className="miniplayer__debug">
          {debugInfo.length ? debugInfo.map((line, index) => <div key={`${line}-${index}`}>{line}</div>) : <div>debug idle</div>}
        </div>
      )}
    </div>
  );
});
